const YOUTUBE_ID_RE = /^[a-zA-Z0-9_-]{11}$/
const DIRECT_VIDEO_EXT_RE = /\.(mp4|webm|mov|m4v)$/i
const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
  'www.youtu.be',
])
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'])
const LOOM_HOSTS = new Set(['loom.com', 'www.loom.com'])

export function youtubeVideoIdFromUrl(value: string): string | null {
  try {
    const parsed = new URL(value)
    const host = parsed.hostname.toLowerCase()
    if (!YOUTUBE_HOSTS.has(host)) {
      return null
    }
    if (host === 'youtu.be' || host === 'www.youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0] || ''
      return YOUTUBE_ID_RE.test(id) ? id : null
    }
    const parts = parsed.pathname.split('/').filter(Boolean)
    if (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live') {
      const id = parts[1] || ''
      return YOUTUBE_ID_RE.test(id) ? id : null
    }
    const v = parsed.searchParams.get('v') || ''
    return YOUTUBE_ID_RE.test(v) ? v : null
  } catch {
    return null
  }
}

function vimeoVideoIdFromUrl(value: string): string | null {
  try {
    const parsed = new URL(value)
    const host = parsed.hostname.toLowerCase()
    if (!VIMEO_HOSTS.has(host)) {
      return null
    }
    const parts = parsed.pathname.split('/').filter(Boolean)
    const candidate =
      host === 'player.vimeo.com' && parts[0] === 'video' ? parts[1] : parts[0]
    return /^\d{6,12}$/.test(candidate || '') ? candidate : null
  } catch {
    return null
  }
}

function loomVideoIdFromUrl(value: string): string | null {
  try {
    const parsed = new URL(value)
    const host = parsed.hostname.toLowerCase()
    if (!LOOM_HOSTS.has(host)) {
      return null
    }
    const parts = parsed.pathname.split('/').filter(Boolean)
    if (parts[0] === 'share' || parts[0] === 'embed') {
      return parts[1] && /^[a-zA-Z0-9]{8,}$/.test(parts[1]) ? parts[1] : null
    }
    return null
  } catch {
    return null
  }
}

function isDirectVideoUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    const path = parsed.pathname.split('/').pop() || ''
    return DIRECT_VIDEO_EXT_RE.test(path)
  } catch {
    return false
  }
}

export function isKnowledgeMediaFile(
  mime?: string | null,
  filename?: string | null,
): boolean {
  const type = (mime || '').toLowerCase()
  const name = (filename || '').toLowerCase()
  if (type.startsWith('video/') || type.startsWith('audio/')) {
    return true
  }
  return /\.(mp4|webm|mov|m4v|mp3|wav|m4a)$/i.test(name)
}

export function isKnowledgeAudioFile(
  mime?: string | null,
  filename?: string | null,
): boolean {
  const type = (mime || '').toLowerCase()
  const name = (filename || '').toLowerCase()
  if (type.startsWith('audio/')) {
    return true
  }
  return /\.(mp3|wav|m4a)$/i.test(name)
}

export function isKnowledgeVideoUrl(url: string | null | undefined): boolean {
  const value = (url || '').trim()
  if (!value) {
    return false
  }
  return (
    Boolean(youtubeVideoIdFromUrl(value)) ||
    Boolean(vimeoVideoIdFromUrl(value)) ||
    Boolean(loomVideoIdFromUrl(value)) ||
    isDirectVideoUrl(value)
  )
}

export type KnowledgeVideoEmbed =
  | { kind: 'iframe'; src: string }
  | { kind: 'file'; src: string }

export function parseVideoTimestampLabel(
  value: string | null | undefined,
): number | null {
  const raw = (value || '').trim()
  if (!raw) {
    return null
  }
  const parts = raw.split(':')
  if (parts.length < 2 || parts.length > 3) {
    return null
  }
  const nums = parts.map((part) => Number(part))
  if (nums.some((num) => !Number.isFinite(num) || num < 0)) {
    return null
  }
  if (parts.length === 2) {
    return Math.floor(nums[0] * 60 + nums[1])
  }
  return Math.floor(nums[0] * 3600 + nums[1] * 60 + nums[2])
}

export function knowledgeVideoUrlAtTime(
  url: string,
  timestampLabel: string | null | undefined,
): string {
  const seconds = parseVideoTimestampLabel(timestampLabel)
  if (seconds == null || seconds <= 0) {
    return url
  }
  try {
    const parsed = new URL(url)
    if (youtubeVideoIdFromUrl(url)) {
      parsed.searchParams.set('t', String(seconds))
      return parsed.toString()
    }
    if (vimeoVideoIdFromUrl(url)) {
      parsed.hash = `t=${seconds}s`
      return parsed.toString()
    }
    if (isDirectVideoUrl(url)) {
      parsed.hash = `t=${seconds}`
      return parsed.toString()
    }
    return url
  } catch {
    return url
  }
}

export function knowledgeVideoEmbed(
  url: string,
  startSeconds?: number | null,
): KnowledgeVideoEmbed | null {
  const start =
    startSeconds != null && startSeconds > 0 ? Math.floor(startSeconds) : null
  const youtubeId = youtubeVideoIdFromUrl(url)
  if (youtubeId) {
    return {
      kind: 'iframe',
      src: start
        ? `https://www.youtube.com/embed/${youtubeId}?start=${start}`
        : `https://www.youtube.com/embed/${youtubeId}`,
    }
  }
  const vimeoId = vimeoVideoIdFromUrl(url)
  if (vimeoId) {
    return {
      kind: 'iframe',
      src: start
        ? `https://player.vimeo.com/video/${vimeoId}#t=${start}s`
        : `https://player.vimeo.com/video/${vimeoId}`,
    }
  }
  const loomId = loomVideoIdFromUrl(url)
  if (loomId) {
    return { kind: 'iframe', src: `https://www.loom.com/embed/${loomId}` }
  }
  if (isDirectVideoUrl(url) || url.startsWith('blob:')) {
    return {
      kind: 'file',
      src: start ? `${url}#t=${start}` : url,
    }
  }
  return null
}
